import React, { useState, Children } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
interface AddRoomFormProps {
  onSave: (roomData: any) => void;
  onCancel: () => void;
}
export function AddRoomForm({
  onSave,
  onCancel
}: AddRoomFormProps) {
  const [roomData, setRoomData] = useState({
    name: '',
    category: '',
    code: '',
    description: '',
    isSmoking: false,
    isAccessible: false,
    isActive: false,
    view: '',
    size: '',
    amenities: [] as string[],
    baseAdultsOccupancy: 2,
    baseChildrenOccupancy: 0,
    maxOccupancy: 2,
    allowAdditionalAdults: false,
    allowAdditionalChildren: false,
    bedConfigurations: [{
      type: '',
      count: 1
    }]
  });
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const amenityOptions = ['Wi-Fi', 'TV', 'Minibar', 'Air Conditioning', 'Balcony', 'Terrace', 'Ocean View', 'Private Pool', 'Jacuzzi', 'Slippers', 'Desk', 'Dining Area', 'Interconnecting Rooms', 'Non-Smoking', 'Beach Access', 'Breakfast Included'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const addBedConfig = () => {
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: [...prev.bedConfigurations, {
        type: '',
        count: 1
      }]
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-2910">
      <div className="flex items-center" data-id="element-2911">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-2912">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-2913" />
          <span data-id="element-2914">Add Room</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2915">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2916">
          <div data-id="element-2917">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2918">
              Room name<span className="text-red-500" data-id="element-2919">*</span>
            </label>
            <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2920" />
          </div>
          <div data-id="element-2921">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2922">
              Room category<span className="text-red-500" data-id="element-2923">*</span>
            </label>
            <div className="relative" data-id="element-2924">
              <select id="category" name="category" value={roomData.category} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-2925">
                <option value="" data-id="element-2926">Select room category</option>
                {roomCategories.map(category => <option key={category} value={category} data-id="element-2927">
                    {category.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-2928">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-2929">
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-2930" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-2931">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2932">
            Room code
          </label>
          <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} placeholder="Enter room code" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2933" />
        </div>
        <div className="mb-6" data-id="element-2934">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2935">
            Description
          </label>
          <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} placeholder="Enter room description" rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2936" />
        </div>
        <div className="flex flex-wrap gap-6 mb-6" data-id="element-2937">
          <div className="flex items-center" data-id="element-2938">
            <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2939" />
            <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-2940">
              Smoking
            </label>
          </div>
          <div className="flex items-center" data-id="element-2941">
            <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2942" />
            <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-2943">
              Accessibility
            </label>
          </div>
          <div className="flex items-center" data-id="element-2944">
            <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2945" />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-2946">
              Status
            </label>
          </div>
        </div>
        {/* Room View and Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2947">
          <div data-id="element-2948">
            <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2949">
              Room view<span className="text-red-500" data-id="element-2950">*</span>
            </label>
            <div className="relative" data-id="element-2951">
              <select id="view" name="view" value={roomData.view} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-2952">
                <option value="" data-id="element-2953">Select room view</option>
                {roomViews.map(view => <option key={view} value={view} data-id="element-2954">
                    {view.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-2955">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-2956">
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-2957" />
                </svg>
              </div>
            </div>
          </div>
          <div data-id="element-2958">
            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2959">
              Room size (sq m)
            </label>
            <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2960" />
          </div>
        </div>
        {/* Room Amenities */}
        <div className="mb-6" data-id="element-2961">
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2962">
            Room amenities
          </label>
          <select id="amenities" name="amenities" multiple value={roomData.amenities} onChange={e => {
          const options = Array.from(e.target.selectedOptions, option => option.value);
          setRoomData(prev => ({
            ...prev,
            amenities: options
          }));
        }} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2963">
            {amenityOptions.map(amenity => <option key={amenity} value={amenity} data-id="element-2964">
                {amenity}
              </option>)}
          </select>
        </div>
        {/* Occupancy Settings */}
        <div className="mb-6" data-id="element-2965">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-2966">
            Occupancy Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-2967">
            <div data-id="element-2968">
              <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2969">
                Base Adults Occupancy<span className="text-red-500" data-id="element-2970">*</span>
              </label>
              <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2971" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-2972">
                Standard number of allowed adults for this room
              </p>
            </div>
            <div data-id="element-2973">
              <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2974">
                Base Children Occupancy<span className="text-red-500" data-id="element-2975">*</span>
              </label>
              <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2976" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-2977">
                Standard number of allowed children for this room
              </p>
            </div>
            <div data-id="element-2978">
              <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2979">
                Maximum Occupancy<span className="text-red-500" data-id="element-2980">*</span>
              </label>
              <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2981" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-2982">
                Maximum number of guests allowed
              </p>
            </div>
          </div>
          <div className="space-y-2 mb-4" data-id="element-2983">
            <div className="flex items-center" data-id="element-2984">
              <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2985" />
              <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-2986">
                Allow additional adults
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-2987">
              Allow additional adults (not exceeding total occupancy limit)
            </p>
            <div className="flex items-center mt-2" data-id="element-2988">
              <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2989" />
              <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-2990">
                Allow additional children
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-2991">
              Allow additional children (not exceeding total occupancy limit)
            </p>
          </div>
          {/* Occupancy Summary */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-2992">
            <div className="flex" data-id="element-2993">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-2994" />
              <div data-id="element-2995">
                <p className="text-sm text-red-700" data-id="element-2996">
                  This room has a base occupancy of{' '}
                  {roomData.baseAdultsOccupancy} Adults and{' '}
                  {roomData.baseChildrenOccupancy} Children with a total maximum
                  occupancy of {roomData.maxOccupancy}.
                </p>
                <p className="text-sm text-red-700" data-id="element-2997">
                  Additional adults are not allowed.
                </p>
                <p className="text-sm text-red-700" data-id="element-2998">
                  Additional children are not allowed.
                </p>
                <p className="text-sm text-red-700" data-id="element-2999">
                  Additional cost per child or adults on top of the base
                  occupancy can be configured in rates.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bed Configuration */}
        <div className="mb-6" data-id="element-3000">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-3001">
            Bed configuration<span className="text-red-500" data-id="element-3002">*</span>
          </h3>
          {roomData.bedConfigurations.map((config, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-3003">
              <div data-id="element-3004">
                <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3005">
                  Bed type
                </label>
                <div className="relative" data-id="element-3006">
                  <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-3007">
                    <option value="" data-id="element-3008">Select bed type</option>
                    {bedTypes.map(type => <option key={type} value={type} data-id="element-3009">
                        {type}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-3010">
                    <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-3011">
                      <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-3012" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center" data-id="element-3013">
                <div className="flex-grow" data-id="element-3014">
                  <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3015">
                    Bed count
                  </label>
                  <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-3016" />
                </div>
                {index > 0 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-3017">
                    <TrashIcon size={18} data-id="element-3018" />
                  </button>}
              </div>
            </div>)}
          <button type="button" onClick={addBedConfig} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-3019">
            <PlusIcon size={16} className="mr-1" data-id="element-3020" />
            Add bed configuration
          </button>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end" data-id="element-3021">
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-3022">
            Save
          </button>
        </div>
      </form>
    </div>;
}